import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, Code, Delete } from '@mui/icons-material';
import {
  ApiDevicesBatteryQuerySettings,
  ApiDevicesSignalMetric,
  ApiDevicesSignalQuerySettings,
  ApiDevicesTransmissionsMetric,
  MutateWidgetPayload,
} from '@thingslog/repositories';
import React, { FC } from 'react';
import { Control, Controller, ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { QuerySelectOption, TimespanMode, WidgetFieldPath, WidgetRegisterOptions } from './models';
import { TimespanPickerFields } from '../../timespan';

interface QuerySectionProps {
  title: string;
  description?: string;
  tourId?: string;
  children: React.ReactNode;
}

interface SelectFieldProps {
  control: Control<MutateWidgetPayload>;
  name: WidgetFieldPath;
  label: string;
  options: QuerySelectOption[];
  disabled?: boolean;
  loading?: boolean;
  helperText?: string;
  rules?: WidgetRegisterOptions;
  tourId?: string;
}

interface MultiSelectFieldProps extends SelectFieldProps {
  emptyText?: string;
}

interface TimespanFieldsProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  timespanType: TimespanMode;
  handleTimespanTypeChange: (type: TimespanMode) => void;
}

interface PreferFlowFieldProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  tourId?: string;
}

interface BatteryRangeFieldProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  tourId?: string;
}

interface SignalMetricsFieldProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
}

interface TransmissionsMetricsFieldProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
}

interface LevelRangeFieldProps extends BatteryRangeFieldProps {
  title: string;
  description: string;
}

type DeviceLevelQuerySettings = ApiDevicesBatteryQuerySettings | ApiDevicesSignalQuerySettings;

export const QuerySection: FC<QuerySectionProps> = ({
  title,
  description,
  tourId,
  children,
}: QuerySectionProps) => (
  <section
    className="flex min-w-0 flex-col gap-3 rounded-md border border-solid border-gray-200 p-3"
    data-dashboard-tour={tourId}
  >
    <div className="flex flex-col gap-1">
      <Typography variant="subtitle2">{title}</Typography>
      {description && (
        <Typography variant="caption" className="text-gray-600">
          {description}
        </Typography>
      )}
    </div>
    {children}
  </section>
);

const filterOptions = (
  options: QuerySelectOption[],
  { inputValue }: { inputValue: string }
): QuerySelectOption[] => {
  const searchTerm = inputValue.toLowerCase();

  return options.filter((option: QuerySelectOption): boolean =>
    option.searchText.includes(searchTerm)
  );
};

const getOptionForValue = (
  value: string | null | undefined,
  options: QuerySelectOption[],
  fallbackLabel?: string
): QuerySelectOption | null => {
  if (!value) {
    return null;
  }

  return (
    options.find((option: QuerySelectOption): boolean => option.value === value) ?? {
      value,
      label: fallbackLabel ?? value,
      searchText: value.toLowerCase(),
    }
  );
};

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: QuerySelectOption
): JSX.Element => (
  <li {...props}>
    {option.isVariable && <Code fontSize="small" className="mr-2 text-blue-600" />}
    {option.label}
  </li>
);

export const QuerySingleSelectField: FC<SelectFieldProps> = ({
  control,
  name,
  label,
  options,
  disabled = false,
  loading = false,
  helperText,
  rules,
  tourId,
}: SelectFieldProps) => {
  const { t } = useTranslation();

  return (
    <div data-dashboard-tour={tourId}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({
          field,
          fieldState,
        }: {
          field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
          fieldState: ControllerFieldState;
        }): JSX.Element => (
          <Autocomplete<QuerySelectOption, false, false, false>
            value={getOptionForValue(field.value as string, options)}
            onChange={(_: React.SyntheticEvent, newValue: QuerySelectOption | null): void => {
              field.onChange(newValue?.value ?? '');
            }}
            options={options}
            getOptionLabel={(option: QuerySelectOption): string => option.label}
            filterOptions={filterOptions}
            isOptionEqualToValue={(option: QuerySelectOption, value: QuerySelectOption): boolean =>
              option.value === value.value
            }
            renderOption={renderOption}
            disabled={disabled || loading}
            loading={loading}
            renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
              <TextField
                {...params}
                label={label}
                size="small"
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ??
                  (loading ? t('dashboard_loading', 'Loading...') : helperText)
                }
              />
            )}
          />
        )}
      />
    </div>
  );
};

export const QueryMultiSelectField: FC<MultiSelectFieldProps> = ({
  control,
  name,
  label,
  options,
  disabled = false,
  loading = false,
  helperText,
  emptyText,
  rules,
  tourId,
}: MultiSelectFieldProps) => {
  const { t } = useTranslation();

  return (
    <div data-dashboard-tour={tourId}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({
          field,
          fieldState,
        }: {
          field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
          fieldState: ControllerFieldState;
        }): JSX.Element => {
          const values = (Array.isArray(field.value) ? field.value : []) as string[];
          const selectedOptions = values.map((value: string): QuerySelectOption => {
            return getOptionForValue(value, options) as QuerySelectOption;
          });

          return (
            <div className="flex flex-col gap-1">
              <Autocomplete<QuerySelectOption, true, false, false>
                multiple
                value={selectedOptions}
                onChange={(_: React.SyntheticEvent, newValue: QuerySelectOption[]): void => {
                  field.onChange(newValue.map((option: QuerySelectOption): string => option.value));
                }}
                options={options}
                getOptionLabel={(option: QuerySelectOption): string => option.label}
                filterOptions={filterOptions}
                isOptionEqualToValue={(
                  option: QuerySelectOption,
                  value: QuerySelectOption
                ): boolean => option.value === value.value}
                renderOption={renderOption}
                disabled={disabled || loading}
                loading={loading}
                renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                  <TextField
                    {...params}
                    label={label}
                    size="small"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ??
                      (loading ? t('dashboard_loading', 'Loading...') : helperText)
                    }
                  />
                )}
              />
              {emptyText && values.length === 0 && (
                <Typography variant="caption" className="text-gray-600">
                  {emptyText}
                </Typography>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export const PreferFlowField: FC<PreferFlowFieldProps> = ({
  control,
  disabled = false,
}: PreferFlowFieldProps) => {
  const { t } = useTranslation();

  return (
    <QuerySection
      title={t('dashboard_data_type', 'Data Type')}
      description={t(
        'dashboard_data_type_description',
        'Choose whether readings or flow values should be preferred.'
      )}
      tourId="query-prefer-flow"
    >
      <Controller
        name={'settings.query.preferFlow' as WidgetFieldPath}
        control={control}
        render={({
          field,
        }: {
          field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
        }): JSX.Element => (
          <ToggleButtonGroup
            value={field.value ? 'flow' : 'readings'}
            exclusive
            fullWidth
            size="small"
            disabled={disabled}
            onChange={(
              _event: React.MouseEvent<HTMLElement>,
              newValue: 'readings' | 'flow' | null
            ): void => {
              if (newValue) {
                field.onChange(newValue === 'flow');
              }
            }}
          >
            <ToggleButton value="readings">{t('dashboard_readings', 'Readings')}</ToggleButton>
            <ToggleButton value="flow">{t('flow', 'Flow')}</ToggleButton>
          </ToggleButtonGroup>
        )}
      />
    </QuerySection>
  );
};

export const TimespanFields: FC<TimespanFieldsProps> = ({
  control,
  disabled = false,
  timespanType,
  handleTimespanTypeChange,
}: TimespanFieldsProps) => {
  const { t } = useTranslation();

  return (
    <TimespanPickerFields<MutateWidgetPayload>
      control={control}
      mode={timespanType}
      title={t('dashboard_time_period', 'Time Period')}
      description={t(
        'dashboard_query_time_period_description',
        'Use the dashboard period or override it for this widget query.'
      )}
      className="flex min-w-0 flex-col gap-3 rounded-md border border-solid border-gray-200 p-3"
      predefinedLabel={t('dashboard_period_preset', 'Preset')}
      tourTargetBase="query-timespan"
      predefinedName={'settings.query.timespan.name' as WidgetFieldPath}
      fromDateName={'settings.query.timespan.fromDate' as WidgetFieldPath}
      toDateName={'settings.query.timespan.toDate' as WidgetFieldPath}
      disabled={disabled}
      modeOptions={[
        { value: 'none', label: t('dashboard_period_dashboard', 'Dashboard') },
        { value: 'predefined', label: t('dashboard_period_preset', 'Preset') },
        { value: 'custom', label: t('dashboard_period_custom', 'Custom') },
      ]}
      onModeChange={(mode: TimespanMode): void => handleTimespanTypeChange(mode)}
    />
  );
};

export const LevelRangeField: FC<LevelRangeFieldProps> = ({
  control,
  disabled = false,
  tourId,
  title,
  description,
}: LevelRangeFieldProps) => (
  <QuerySection title={title} description={description} tourId={tourId}>
    <Controller
      name={'settings.query' as WidgetFieldPath}
      control={control}
      render={({
        field,
      }: {
        field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
      }): JSX.Element => {
        const query = field.value as DeviceLevelQuerySettings;
        const fromLevel = Number(query.fromLevel ?? 0);
        const toLevel = Number(query.toLevel ?? 100);

        return (
          <div className="px-2 pt-6">
            <Slider
              value={[fromLevel, toLevel]}
              min={0}
              max={100}
              valueLabelDisplay="on"
              disabled={disabled}
              onChange={(_: Event, value: number | number[]): void => {
                const [newFromLevel, newToLevel] = value as number[];
                field.onChange({
                  ...query,
                  fromLevel: newFromLevel.toString(),
                  toLevel: newToLevel.toString(),
                });
              }}
            />
            <div className="flex justify-between text-xs text-gray-600">
              <span>{fromLevel}%</span>
              <span>{toLevel}%</span>
            </div>
          </div>
        );
      }}
    />
  </QuerySection>
);

export const BatteryRangeField: FC<BatteryRangeFieldProps> = ({
  control,
  disabled = false,
}: BatteryRangeFieldProps) => {
  const { t } = useTranslation();

  return (
    <LevelRangeField
      control={control}
      disabled={disabled}
      title={t('dashboard_battery_level', 'Battery Level')}
      description={t(
        'dashboard_battery_level_description',
        'Limit the widget to a battery percentage range.'
      )}
      tourId="query-battery-range"
    />
  );
};

export const SignalRangeField: FC<BatteryRangeFieldProps> = ({
  control,
  disabled = false,
}: BatteryRangeFieldProps) => {
  const { t } = useTranslation();

  return (
    <LevelRangeField
      control={control}
      disabled={disabled}
      title={t('dashboard_signal_level', 'Signal Level')}
      description={t(
        'dashboard_signal_level_description',
        'Limit the widget to a signal percentage range.'
      )}
      tourId="query-signal-range"
    />
  );
};

export const SignalMetricsField: FC<SignalMetricsFieldProps> = ({
  control,
  disabled = false,
}: SignalMetricsFieldProps) => {
  const { t } = useTranslation();

  return (
    <QuerySection
      title={t('dashboard_signal_values', 'Signal Values')}
      description={t(
        'dashboard_signal_values_description',
        'Choose which signal values should be shown by the widget.'
      )}
      tourId="query-signal-values"
    >
      <Controller
        name={'settings.query.metrics' as WidgetFieldPath}
        control={control}
        rules={{
          validate: (value: unknown): string | true => {
            return Array.isArray(value) && value.length > 0
              ? true
              : t('dashboard_signal_value_required', 'Select at least one signal value');
          },
        }}
        render={({
          field,
          fieldState,
        }: {
          field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
          fieldState: ControllerFieldState;
        }): JSX.Element => {
          const selectedMetrics = (
            Array.isArray(field.value) && field.value.length > 0 ? field.value : ['level']
          ) as ApiDevicesSignalMetric[];
          const updateMetric = (metric: ApiDevicesSignalMetric, checked: boolean): void => {
            const nextMetrics = checked
              ? Array.from(new Set([...selectedMetrics, metric]))
              : selectedMetrics.filter(
                  (selectedMetric: ApiDevicesSignalMetric): boolean => selectedMetric !== metric
                );

            if (nextMetrics.length > 0) {
              field.onChange(nextMetrics);
            }
          };

          return (
            <div className="flex flex-col gap-1">
              <FormGroup row className="gap-3">
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('level')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('level', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>{t('dashboard_signal_level', 'Signal level')}</span>
                      <span className="text-xs text-gray-500">%</span>
                    </span>
                  }
                />
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('rssi')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('rssi', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>RSSI</span>
                      <span className="text-xs text-gray-500">dBm</span>
                    </span>
                  }
                />
              </FormGroup>
              {fieldState.error && (
                <Typography color="error" variant="caption">
                  {fieldState.error.message}
                </Typography>
              )}
            </div>
          );
        }}
      />
    </QuerySection>
  );
};

export const TransmissionsMetricsField: FC<TransmissionsMetricsFieldProps> = ({
  control,
  disabled = false,
}: TransmissionsMetricsFieldProps) => {
  const { t } = useTranslation();

  return (
    <QuerySection
      title={t('dashboard_transmission_values', 'Transmission Values')}
      description={t(
        'dashboard_transmission_values_description',
        'Choose which transmission values should be shown by the widget.'
      )}
      tourId="query-transmission-values"
    >
      <Controller
        name={'settings.query.metrics' as WidgetFieldPath}
        control={control}
        rules={{
          validate: (value: unknown): string | true => {
            return Array.isArray(value) && value.length > 0
              ? true
              : t(
                  'dashboard_transmission_value_required',
                  'Select at least one transmission value'
                );
          },
        }}
        render={({
          field,
          fieldState,
        }: {
          field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
          fieldState: ControllerFieldState;
        }): JSX.Element => {
          const selectedMetrics = (
            Array.isArray(field.value) && field.value.length > 0 ? field.value : ['days']
          ) as ApiDevicesTransmissionsMetric[];
          const updateMetric = (metric: ApiDevicesTransmissionsMetric, checked: boolean): void => {
            const nextMetrics = checked
              ? Array.from(new Set([...selectedMetrics, metric]))
              : selectedMetrics.filter(
                  (selectedMetric: ApiDevicesTransmissionsMetric): boolean =>
                    selectedMetric !== metric
                );

            if (nextMetrics.length > 0) {
              field.onChange(nextMetrics);
            }
          };

          return (
            <div className="flex flex-col gap-1">
              <FormGroup row className="gap-3">
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('days')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('days', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>{t('dashboard_days', 'Days')}</span>
                    </span>
                  }
                />
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('transmissions')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('transmissions', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>{t('dashboard_transmissions', 'Transmissions')}</span>
                    </span>
                  }
                />
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('count')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('count', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>{t('function_count', 'Count')}</span>
                    </span>
                  }
                />
                <FormControlLabel
                  className="m-0"
                  control={
                    <Checkbox
                      checked={selectedMetrics.includes('transmitting')}
                      disabled={disabled}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        updateMetric('transmitting', event.target.checked);
                      }}
                    />
                  }
                  label={
                    <span className="flex min-w-[112px] items-center justify-between gap-2 rounded-md border border-solid border-gray-200 px-2 py-1 text-sm">
                      <span>{t('dashboard_transmitting', 'Transmitting')}</span>
                    </span>
                  }
                />
              </FormGroup>
              {fieldState.error && (
                <Typography color="error" variant="caption">
                  {fieldState.error.message}
                </Typography>
              )}
            </div>
          );
        }}
      />
    </QuerySection>
  );
};

export const AddQueryRowButton: FC<{
  label: string;
  disabled?: boolean;
  tourId?: string;
  onClick: () => void;
}> = ({
  label,
  disabled = false,
  tourId,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  tourId?: string;
  onClick: () => void;
}) => (
  <Button
    startIcon={<Add />}
    variant="outlined"
    size="small"
    disabled={disabled}
    data-dashboard-tour={tourId}
    onClick={onClick}
  >
    {label}
  </Button>
);

export const DeleteQueryRowButton: FC<{
  label: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({
  label,
  disabled = false,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <IconButton aria-label={label} color="error" disabled={disabled} onClick={onClick}>
    <Delete fontSize="small" />
  </IconButton>
);
